import { useEffect, useMemo, useRef, useState } from "react";

import {
  faCube,
  faLock,
  faLockOpen,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { find } from "lodash-es";
import { useLocation, useNavigate } from "react-router-dom";
import { twJoin } from "tailwind-merge";
import { useSessionStorage } from "usehooks-ts";

import { GenericObject } from "src/api/flowTypes";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import { Divider } from "src/base-components/Divider";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Cell, TableComp } from "src/base-components/Table";
import {
  PropertyDefinitionOutput,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { parseJsonOrReturnAsIs } from "src/datasets/DatasetTable/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useSidepaneContext } from "src/entities/entityView/DecisionsSidePane/SidePaneContext";
import { JsonContent } from "src/entities/entityView/EntityDetailsSidePane";
import { EntityDetailsWindowPill } from "src/entities/entityView/EntityDetailsWindowPill";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { TIME_WINDOW_SESSION_STORAGE_KEY } from "src/eventsCatalogue/EventsCatalogue";
import {
  DeserializedEvent,
  useEvent,
  useEventSchema,
  useRevealEventField,
} from "src/eventsCatalogue/queries";
import { EnumPill, PropertyWithIcon } from "src/eventsCatalogue/tableConfig";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useEnvironment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { EventsCatalogueParams, getUrlToEventsPage } from "src/router/urls";
import { formatNumber } from "src/utils/numbers";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EventViewPaneProps = {
  eventType: string;
  eventId: string;
  onClose: () => void;
};

type EventViewTableData = {
  label: string;
  value: string;
  type: PropertyDefinitionOutputTypeEnum;
  render: (value: unknown) => React.ReactNode;
  sensitive?: boolean;
  field: string;
};

type EventFieldProps = {
  value: unknown;
  isLoading?: boolean;
  isSensitive?: boolean;
  render: (value: unknown) => React.ReactNode;
  onToggle: () => void;
  isRevealed: boolean;
};

const EventField: React.FC<EventFieldProps> = ({
  value,
  isLoading = false,
  isSensitive = false,
  render,
  onToggle,
  isRevealed,
}) => {
  const { sensitiveFields } = useCapabilities();
  const isRevealable = isSensitive && sensitiveFields.canReveal;

  return (
    <div className="flex w-full items-center gap-x-1">
      <div className="min-w-0 flex-1">
        {isLoading ? (
          <SkeletonPlaceholder height="h-4" width="w-32" />
        ) : (
          render(value)
        )}
      </div>
      {isRevealable && !isLoading && (
        <Tooltip
          body={
            !isRevealed &&
            "Unmask the sensitive field. A log of this action will be recorded in the Audit Log. The field will revert back to being masked when you leave the page"
          }
          placement="right"
          title={isRevealed ? "Hide sensitive data" : "Reveal masked field"}
          asChild
        >
          <Icon
            color="text-gray-500 hover:text-gray-700"
            icon={isRevealed ? faLockOpen : faLock}
            size="xs"
            onClick={onToggle}
          />
        </Tooltip>
      )}
    </div>
  );
};

type EventDetailsCellProps = {
  info: CellContext<EventViewTableData, unknown>;
  event: DeserializedEvent | undefined;
  environment: string;
  eventType: string;
};

const EventDetailsCell: React.FC<EventDetailsCellProps> = ({
  info,
  event,
  environment,
  eventType,
}) => {
  const row = info.row.original;
  const isSensitive = row.sensitive ?? false;
  const fieldName = row.field;
  const maskedValue = info.getValue();
  const eventIdVal = event?.payload?.id;
  const [isRevealed, setIsRevealed] = useState(false);

  const { data: revealData, isLoading } = useRevealEventField({
    env: environment,
    eventId: eventIdVal ?? "",
    eventType,
    field: fieldName,
    enabled: isSensitive && !!eventIdVal && isRevealed,
    timestamp: event?.payload?.timestamp ?? null,
  });

  const renderValue = (value: unknown) => (
    <Tooltip
      disabled={typeof value !== "string"}
      placement="top"
      title={typeof value === "string" ? value : undefined}
      asChild
    >
      <div
        className={twJoin(
          "truncate",
          isSensitive && !isRevealed && "font-code-12 text-gray-500",
        )}
      >
        {row.render(value)}
      </div>
    </Tooltip>
  );

  const handleToggle = () => {
    setIsRevealed((prevRevealed) => !prevRevealed);
  };

  return (
    <Cell className="max-w-full" info={info}>
      <EventField
        isLoading={isLoading}
        isRevealed={isRevealed}
        isSensitive={isSensitive && !!eventIdVal}
        render={renderValue}
        value={isRevealed ? revealData?.value : maskedValue}
        onToggle={handleToggle}
      />
    </Cell>
  );
};

export const EventViewPane: React.FC<EventViewPaneProps> = ({
  eventType,
  eventId,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { workspace } = useWorkspaceContext();
  const [environment] = useEnvironment();
  const previousEnv = useRef(environment);
  const { orgId, wsId } = useParamsDecode<EventsCatalogueParams>();
  const { data: entitiesSchemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });
  const [timeWindow] = useSessionStorage(TIME_WINDOW_SESSION_STORAGE_KEY, {
    from: subDays(startOfToday(), 7).toISOString(),
    to: endOfToday().toISOString(),
  });

  const { data: event, isLoading: isEventLoading } = useEvent(
    {
      eventType,
      eventId,
      environment,
      timeWindow: {
        from: timeWindow.from,
        to: timeWindow.to,
      },
    },
    {
      // Disable the query when environment changes and we're on the event detail page
      enabled: !(
        previousEnv.current !== environment &&
        location.pathname.includes("/event/")
      ),
    },
  );
  const { data: schema, isLoading: isSchemaLoading } =
    useEventSchema(eventType);

  useEffect(() => {
    if (previousEnv.current !== environment) {
      if (location.pathname.includes("/event/")) {
        const timeoutId = setTimeout(() => {
          //make the transition feel less abrupt and flashy
          navigate(
            getUrlToEventsPage({
              orgId,
              wsId,
              eventType,
            }),
          );
        }, 200);

        return () => clearTimeout(timeoutId);
      }
      previousEnv.current = environment;
    }
  }, [environment, navigate, eventType, eventId, orgId, wsId, location]);

  const transformEventToRows = (): EventViewTableData[] => {
    if (!event?.payload || !schema) return [];

    const eventData = event.payload;
    const rows: EventViewTableData[] = [
      {
        label: "ID",
        value: eventData.id,
        type: PropertyDefinitionOutputTypeEnum.STRING,
        render: (value: unknown) => (
          <Pill dataLoc="event-id-pill-sidepane" size="sm" variant="gray">
            <Pill.Text fontType="code">{value as string}</Pill.Text>
            <CopyTextIcon
              dataLoc="copy-id-icon"
              feedback="inline"
              size="3xs"
              value={value as string}
            />
          </Pill>
        ),
        field: "id",
      },
      {
        label: "Timestamp",
        value: eventData.timestamp,
        type: PropertyDefinitionOutputTypeEnum.DATETIME,
        render: (value: unknown) => <DisplayDateTime value={value as string} />,
        field: "timestamp",
      },
    ];

    Object.entries(schema.properties)
      .filter(([key]) => !["id", "timestamp"].includes(key))
      .forEach(([key, value]: [string, PropertyDefinitionOutput]) => {
        const cellValue = eventData[key];
        let renderer;

        if (
          value.type === PropertyDefinitionOutputTypeEnum.DATETIME ||
          value.type === PropertyDefinitionOutputTypeEnum.DATE
        ) {
          renderer = (val: unknown) => {
            if (!val) return null;
            return value.type === PropertyDefinitionOutputTypeEnum.DATETIME ? (
              <DisplayDateTime value={val as string} />
            ) : (
              <DisplayDate value={val as string} />
            );
          };
        } else if (value.type === PropertyDefinitionOutputTypeEnum.INTEGER) {
          renderer = (val: unknown) => {
            if (!val) return null;
            return (
              <div className="font-code-13">{formatNumber(val as number)}</div>
            );
          };
        } else if (value.type === PropertyDefinitionOutputTypeEnum.NUMBER) {
          renderer = (val: unknown) => {
            if (!val) return null;
            return (
              <div className="font-code-13">{formatNumber(val as number)}</div>
            );
          };
        } else if (value.type === PropertyDefinitionOutputTypeEnum.ENUM) {
          renderer = (val: unknown) => {
            const parsedCellValue = parseJsonOrReturnAsIs(val as string);
            const enumValue = find(value.enum_schema?.values, [
              "value",
              parsedCellValue,
            ]);
            if (!enumValue) return null;
            return <EnumPill enumValue={enumValue} />;
          };
        } else if (value.type === PropertyDefinitionOutputTypeEnum.RELATION) {
          renderer = (val: unknown) => {
            if (!val) return null;
            const value = val as DeserializedEvent["payload"];
            const relationSchema = findSchema(value._type, entitiesSchemasData);
            return (
              <EntityDetailsWindowPill
                entityIcon={
                  relationSchema ? getSchemaIcon(relationSchema) : faCube
                }
                environment={environment}
                fullWidth={false}
                id={value._id}
                name={value._id}
                schemaId={value._type}
              />
            );
          };
        } else if (value.type === PropertyDefinitionOutputTypeEnum.BOOLEAN) {
          renderer = (val: unknown) =>
            val === null || val === undefined
              ? null
              : speakPythonPrimitive(String(val));
        } else if (value.type === PropertyDefinitionOutputTypeEnum.ADDRESS) {
          renderer = (val: unknown) => {
            return (
              <div className="max-w-50">
                <JsonContent
                  content={val as GenericObject}
                  title={value.display_name ?? key}
                />
              </div>
            );
          };
        } else {
          renderer = (val: unknown) => String(val);
        }

        rows.push({
          label:
            value.display_name ?? value.relation_schema?.entity_type ?? key,
          value: cellValue as string,
          type: value.type,
          render: renderer,
          sensitive: value._sensitive ?? false,
          field: key,
        });
      });

    return rows;
  };

  const columnHelper = createColumnHelper<EventViewTableData>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("label", {
        header: "Label",
        cell: (info) => (
          <PropertyWithIcon type={info.row.original.type}>
            {info.getValue()}
          </PropertyWithIcon>
        ),
        maxSize: 130,
        minSize: 130,
        size: 130,
      }),
      columnHelper.accessor("value", {
        header: "Value",
        cell: (info) => (
          <EventDetailsCell
            environment={environment}
            event={event}
            eventType={eventType}
            info={info}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [environment, event, eventType],
  );

  const { width, ref, ResizeHandler } = useSidepaneContext();

  return (
    <div
      ref={ref}
      className="relative flex shrink-0 flex-col gap-y-4 bg-white p-5"
      style={{
        width: `${width}px`,
      }}
    >
      <ResizeHandler />
      <div className="flex items-center justify-between">
        <div>
          {!isEventLoading && event && schema && !isSchemaLoading ? (
            <div className="flex gap-2 font-inter-semibold-13px text-gray-800">
              <div className="h-5 w-5 rounded border border-gray-200">
                <Icon
                  color="text-gray-600"
                  icon={getEventIcon(schema)}
                  size="2xs"
                />
              </div>
              <DisplayDateTime value={event.payload.timestamp} />
            </div>
          ) : (
            <SkeletonPlaceholder height="h-5" />
          )}
        </div>
        <Icon
          color="text-gray-500"
          icon={faTimes}
          size="xs"
          onClick={onClose}
        />
      </div>
      <Divider />
      {schema && (
        <TableComp
          columns={columns}
          data={transformEventToRows()}
          dataLoc="event-details-table"
          frameClassName="[&_thead_tr]:hidden [&_table]:border-b-0"
          isLoading={isEventLoading || isSchemaLoading}
          variant="compact"
          noScroll
        />
      )}
    </div>
  );
};
